import camel from 'lodash-es/camelCase';
import eventbus from '../utils/eventbus';

const toCamel = (value) => camel(value);

const parseAttributeValue = (value) => {
	try {
		value = JSON.parse(value);
	} catch {
		// Ignore JSON parse errors
	}

	return value;
};

const getElementOptions = (el) =>
	Object.keys(el.dataset)
		.filter((key) => key.startsWith('option'))
		.reduce((opts, key) => {
			const id = toCamel(key.substring(6));
			const value = parseAttributeValue(el.dataset[key]);

			opts[id] = value;

			return opts;
		}, {});

// https://vicompany.visualstudio.com/vicompany-npm/_git/module-base
class Base {
	constructor(el, options = {}) {
		this.el = el;
		this.options = options;
		this.eventbus = eventbus;

		if ('MutationObserver' in window) {
			const observer = new MutationObserver((mutations) => {
				mutations.forEach((m) => {
					const added = Array.from(m.addedNodes).filter((n) => n.dataset?.ref);
					const removed = Array.from(m.removedNodes).filter((n) => n.dataset?.ref);

					this.add(added);
					this.remove(removed);
				});
			});

			observer.observe(this.el, {
				childList: true,
			});
		}
	}

	get el() {
		return this._el;
	}

	set el(el) {
		if (!el) {
			throw new Error('Element must be a selector or DOM node');
		}

		// Only 'Elements' have a tagName property: https://developer.mozilla.org/en-US/docs/Web/API/Element
		// Checking with 'el instanceof Element' will fail in Node.js libs and old IE versions.
		// So we do a little bit of 'duck typing'. :)
		if (el.tagName) {
			this._el = el;
		} else if (typeof el === 'string') {
			el = document.querySelector(el);

			if (!el) {
				throw new Error('Element does not exist');
			}

			this._el = el;
		}
	}

	get options() {
		return this._options;
	}

	set options(options) {
		this._options = Object.assign(options, getElementOptions(this.el));
	}

	get refs() {
		return (
			this._refs ||
			(this._refs = this.queryAll('[data-ref]').reduce((refs, el) => {
				const ref = toCamel(el.dataset.ref);

				refs[ref] = el;

				return refs;
			}, {}))
		);
	}

	set refs({ els, isAdding = true }) {
		const refs = this._refs;

		els.forEach((el) => {
			const ref = toCamel(el.dataset.ref);

			if (refs[ref]) {
				if (isAdding) {
					console.warn(`Dupicate ref "${ref}" found`);
				}

				delete refs[ref];
			}

			if (isAdding) {
				refs[ref] = el;
			}
		});
	}

	add(els) {
		this.refs = {
			els,
			isAdding: true,
		};
	}

	remove(els) {
		this.refs = {
			els,
			isAdding: false,
		};
	}

	on(event, el = this.el, options = false) {
		el.addEventListener(event, this, options);
	}

	off(event, el = this.el, options = false) {
		el.removeEventListener(event, this, options);
	}

	// https://medium.com/@WebReflection/dom-handleevent-a-cross-platform-standard-since-year-2000-5bf17287fd38
	handleEvent(e) {
		const { type } = e;
		const handler = `on${type.charAt(0).toUpperCase()}${type.slice(1)}`;

		if (typeof this[handler] !== 'function') {
			console.warn(`Event handler "${handler}" does not exist`);

			return;
		}

		this[handler](e);
	}

	query(selector, el = this.el) {
		return el.querySelector(selector);
	}

	queryAll(selector, el = this.el) {
		return Array.from(el.querySelectorAll(selector));
	}

	start() {
		return this;
	}
}

export default Base;
